import React, { useEffect, useState } from 'react';
import relatorioService, { type PrestacaoRelatoriosCabecalho } from '../../services/relatorioPrestacaoContasService';
import DownloadButtons from './DownloadButtons';
import Button from '../../components/ui/Button';
import { toUpperText } from '../../utils/formatters';
import {
  bodyTextClass, inputClass, labelTextClass, primaryButtonClass, secondaryButtonClass,
  strongTextClass, subtleSurfaceClass, type SectionProps,
} from './shared';

type Props = SectionProps & { cabecalho: PrestacaoRelatoriosCabecalho | null };

const campo = (label: string, value: string, onChange: (v: string) => void, disabled: boolean) => (
  <div>
    <label className={`mb-1 block text-xs font-semibold uppercase tracking-[0.1em] ${labelTextClass}`}>{label}</label>
    <textarea className={inputClass} rows={3} value={value} onChange={(e) => onChange(toUpperText(e.target.value))} disabled={disabled} />
  </div>
);

const Anexo3RelatoNarrativoSection: React.FC<Props> = ({ pcId, podeEditar, cabecalho, onAlterado }) => {
  const [form, setForm] = useState({
    acoesExecutadas: '', alcanceObjetivos: '', conclusao: '', justificativaAtrasos: '', medidasAdotadas: '',
  });
  const [finalizado, setFinalizado] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const carregar = async () => {
    setLoading(true);
    setError(null);
    try {
      const relato = await relatorioService.getRelatoNarrativo(pcId);
      setForm({
        acoesExecutadas: relato.acoesExecutadas || '',
        alcanceObjetivos: relato.alcanceObjetivos || '',
        conclusao: relato.conclusao || '',
        justificativaAtrasos: relato.justificativaAtrasos || '',
        medidasAdotadas: relato.medidasAdotadas || '',
      });
      setFinalizado(relato.finalizado);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Falha ao carregar o relato.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pcId]);

  const salvar = async (finalizar: boolean) => {
    setSaving(true);
    setError(null);
    try {
      const relato = await relatorioService.salvarRelatoNarrativo(pcId, { ...form, finalizar });
      setFinalizado(relato.finalizado);
      onAlterado?.();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Falha ao salvar o relato.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <h2 className={`text-lg font-semibold ${strongTextClass}`}>Anexo 3 · Cumprimento/Execução do Objeto</h2>
          {!loading && (
            <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${
              finalizado
                ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/30'
                : 'bg-amber-50 text-amber-800 ring-1 ring-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/30'
            }`}>
              {finalizado ? 'Finalizado' : 'Rascunho'}
            </span>
          )}
        </div>
        <DownloadButtons pcId={pcId} anexo="3" nomeArquivo="anexo-3-cumprimento-execucao-objeto" comXlsx={false} />
      </div>

      <div className={`rounded-2xl border p-4 text-sm ${subtleSurfaceClass}`}>
        <p className={`mb-2 text-xs font-semibold uppercase tracking-[0.16em] ${labelTextClass}`}>Objeto da parceria (vem do plano de trabalho)</p>
        <p className={bodyTextClass}>{cabecalho?.objetoParceria || 'Não informado no plano de trabalho.'}</p>
      </div>

      <div className={`rounded-2xl border p-4 text-sm ${subtleSurfaceClass}`}>
        <p className={`mb-2 text-xs font-semibold uppercase tracking-[0.16em] ${labelTextClass}`}>Metas previstas e ações programadas (vêm do plano de trabalho)</p>
        {(cabecalho?.metas ?? []).length === 0 ? (
          <p className={bodyTextClass}>Nenhuma meta cadastrada no plano de trabalho.</p>
        ) : (
          <ul className="space-y-3">
            {(cabecalho?.metas ?? []).map((meta) => (
              <li key={meta.metaId}>
                <p className={`font-semibold ${strongTextClass}`}>{meta.titulo}</p>
                {meta.descricao && <p className={bodyTextClass}>{meta.descricao}</p>}
                {(meta.etapas ?? []).length > 0 && (
                  <ul className={`mt-1 list-disc pl-5 ${bodyTextClass}`}>
                    {(meta.etapas ?? []).map((etapa, i) => (
                      <li key={etapa.cronogramaId ?? i}>{etapa.atividade}{etapa.dataPrevista ? ` (previsto para ${etapa.dataPrevista.split('-').reverse().join('/')})` : ''}</li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {error && (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-200">
          {error}
        </div>
      )}

      {loading ? (
        <p className={`text-sm ${bodyTextClass}`}>Carregando...</p>
      ) : (
        <>
          {finalizado && (
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-200">
              Relato finalizado. Você ainda pode editar e salvar novamente.
            </div>
          )}

          {campo('Ações executadas', form.acoesExecutadas, (v) => setForm((p) => ({ ...p, acoesExecutadas: v })), !podeEditar)}
          {campo('Alcance dos objetivos', form.alcanceObjetivos, (v) => setForm((p) => ({ ...p, alcanceObjetivos: v })), !podeEditar)}
          {campo('Conclusão', form.conclusao, (v) => setForm((p) => ({ ...p, conclusao: v })), !podeEditar)}
          {campo('Justificativa de atrasos e/ou ações não cumpridas', form.justificativaAtrasos, (v) => setForm((p) => ({ ...p, justificativaAtrasos: v })), !podeEditar)}
          {campo('Medidas adotadas', form.medidasAdotadas, (v) => setForm((p) => ({ ...p, medidasAdotadas: v })), !podeEditar)}

          {podeEditar && (
            <div className="flex flex-wrap gap-2">
              <Button variant="secondary" onClick={() => salvar(false)} disabled={saving}>
                {saving ? 'Salvando...' : 'Salvar rascunho'}
              </Button>
              <Button variant="primary" onClick={() => salvar(true)} disabled={saving}>
                {saving ? 'Salvando...' : 'Finalizar Anexo 3'}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Anexo3RelatoNarrativoSection;
